/**
 * Action URL Safety Helper
 *
 * Validates URLs embedded in `Notification.actionUrl`. An unsafe URL followed by
 * a portal's `navigate()` call becomes a phishing / open-redirect vector
 * (audit §3.1). Defense-in-depth: rejected at both storage (notification
 * service) and navigation (portals).
 *
 * Decision rules (in order):
 *  1. Relative path starting with `/` (but NOT `//` or `/\`) → safe
 *  2. `http:` or `https:` whose origin matches a monorepo-known domain → safe
 *  3. Anything else → unsafe (javascript:, data:, external, empty, bogus)
 *
 * The allowlist is DERIVED at runtime from the monorepo's own `domains.json`
 * and `ports.json` — no new env var to configure. Any host the monorepo
 * already knows about is implicitly allowed.
 */

import domainsJson from '../../config/domains.json';
import portsJson from '../../config/ports.json';

/**
 * Collect all hosts (with port where applicable) that the monorepo considers
 * "ours". Computed lazily once — the sources are static JSON bundled at import
 * time, so recomputing per call would just waste work.
 */
let cachedAllowedOrigins: Set<string> | null = null;

function getAllowedHosts(): Set<string> {
  if (cachedAllowedOrigins) return cachedAllowedOrigins;

  const hosts = new Set<string>();

  // All localhost:<port> combinations from ports.json
  const localhostHost = portsJson.defaults.host || 'localhost';
  for (const group of Object.values(portsJson.services) as Array<Record<string, { port: number }>>) {
    for (const entry of Object.values(group)) {
      hosts.add(`${localhostHost}:${entry.port}`.toLowerCase());
    }
  }
  // Also allow bare localhost (no port) for proxies / reverse-proxy setups
  hosts.add(localhostHost.toLowerCase());

  // Every known domain from every environment in domains.json.
  // NOTE: if a future tenant whitelabels an IDN domain (e.g., `例え.jp`), the
  // entry MUST be stored in punycode form (`xn--r8jz45g.jp`) because the
  // WHATWG URL parser (Node/Bun/browser) normalizes `parsed.hostname` to
  // punycode before our lookup. A unicode entry in domains.json would never
  // match a real request. Same caveat applies to `www_host` / `alias_host`.
  for (const env of Object.values(domainsJson.environments) as Array<Record<string, unknown>>) {
    const buckets = ['backend', 'portals', 'monitoring', 'internal', 'sidecar'] as const;
    for (const bucket of buckets) {
      const records = env[bucket];
      if (!records || typeof records !== 'object') continue;
      for (const raw of Object.values(records as Record<string, unknown>)) {
        if (typeof raw !== 'string') continue;
        if (raw.startsWith('_')) continue; // skip `_comment` fields
        // Entries may be "host" or "host:port" — normalize to the host[:port] form
        const trimmed = raw.trim().replace(/\/$/, '');
        if (trimmed) hosts.add(trimmed.toLowerCase());
      }
    }
    // Also add the bare top-level host / www_host / alias_host for the env
    for (const key of ['host', 'www_host', 'alias_host'] as const) {
      const value = env[key];
      if (typeof value === 'string' && value) hosts.add(value.trim().toLowerCase());
    }
  }

  cachedAllowedOrigins = hosts;
  return hosts;
}

/**
 * Validate that an `actionUrl` is safe to store on a Notification and
 * subsequently follow via `navigate()` in a portal. Fails closed — any
 * uncertainty returns `false`.
 *
 * @example
 * isSafeActionUrl('/cases/123')                   // true
 * isSafeActionUrl('https://portal.fiops.ai/foo')  // true
 * isSafeActionUrl('https://phishing.com/foo')     // false
 * isSafeActionUrl('javascript:alert(1)')          // false
 * isSafeActionUrl('//evil.com')                   // false
 */
export function isSafeActionUrl(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  if (url.length === 0) return false;

  // Reject anything with whitespace/control chars anywhere — blocks trimmed
  // leading whitespace that hides protocol-relative prefixes, CRLF injection,
  // and newline-based header-injection attempts.
  if (/[\s\u0000-\u001F\u007F]/.test(url)) return false;

  // Relative path: must start with a single "/" and NOT be protocol-relative
  if (url.startsWith('/')) {
    // "//example.com" is protocol-relative → browser resolves to another host
    if (url.startsWith('//')) return false;
    // "/\example.com" — some routers normalize "\" to "/"
    if (url.startsWith('/\\')) return false;
    return true;
  }

  // Absolute URL: must parse, must be http/https, must match allowed host
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  const protocol = parsed.protocol.toLowerCase();
  if (protocol !== 'http:' && protocol !== 'https:') return false;

  // Reject URLs with userinfo — "https://allowed.com@evil.com/" resolves to evil.com
  if (parsed.username || parsed.password) return false;

  const allowed = getAllowedHosts();
  const host = parsed.host.toLowerCase(); // includes :port when non-default
  const hostname = parsed.hostname.toLowerCase();

  return allowed.has(host) || allowed.has(hostname);
}

/**
 * Reset the cached allowlist. Exists for tests that mutate process.env or the
 * underlying JSON; not used by production code.
 * @internal
 */
export function __resetActionUrlAllowlistCache(): void {
  cachedAllowedOrigins = null;
}
